import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare,
  Users,
  Send,
  Wallet,
  ArrowRight,
  AlertCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Gift,
  Star,
} from 'lucide-react';
import GettingStartedChecklist from '../components/GettingStartedChecklist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardOverview() {
  const { tenant, getToken } = useTenant();
  const dashboardData = useDashboardData();

  // Safely destructure with defaults
  const summary = dashboardData?.dashboardSummary;
  const walletStatus = dashboardData?.walletStatus;
  const recentActivity = dashboardData?.recentActivity || [];

  // Email stats (preloaded from context)
  const emailStatsData = dashboardData?.emailStats;

  // Fetch Google review stats (non-blocking)
  const [reviewStats, setReviewStats] = useState(null);
  const fetchReviewStats = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/reviews/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setReviewStats(await res.json());
    } catch (_) {
      // Non-critical — silently ignore
    }
  }, [getToken]);

  useEffect(() => {
    fetchReviewStats();
  }, [fetchReviewStats]);

  // Data is preloaded by DashboardDataContext, so no loading state needed
  // If context data is missing, show skeleton briefly
  if (!summary && !walletStatus) {
    return (
      <div className="p-4 lg:p-8">
        <div className="mb-8">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>

        {/* Stats Grid Skeleton */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6 h-32 flex flex-col justify-between">
              <div className="flex justify-between">
                <Skeleton className="h-12 w-12 rounded-xl" />
                <Skeleton className="h-5 w-5 rounded-full" />
              </div>
              <div>
                <Skeleton className="h-8 w-24 mb-2" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          ))}
        </div>

        {/* Two Column Layout Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  // Wallet-only stats
  const walletBalance = walletStatus?.wallet_balance_cents || summary?.wallet_balance_cents || 0;
  const smsCapacity = walletStatus?.sms_capacity || 0;
  const smsCostCents = walletStatus?.sms_cost_cents || 0;

  const stats = [
    {
      name: 'SMS Remaining',
      value: `${smsCapacity.toLocaleString()} SMS`,
      icon: MessageSquare,
      color: 'orange',
      href: '/dashboard/wallet',
      description: `$${(walletBalance / 100).toFixed(2)} balance`,
    },
    {
      name: 'Customers',
      value: summary?.total_customers?.toLocaleString() || '0',
      icon: Users,
      color: 'green',
      href: '/dashboard/customers',
      description: `${summary?.opted_in_customers || 0} active`,
    },
    {
      name: 'Campaigns Sent',
      value: (summary?.completed_campaigns || summary?.total_campaigns || 0).toLocaleString(),
      icon: Send,
      color: 'purple',
      href: '/dashboard/campaigns',
      description: `${summary?.total_sms_sent || 0} SMS${emailStatsData?.total_sent ? `, ${emailStatsData.total_sent} Email` : ''} delivered`,
    },
    {
      name: 'Google Rating',
      value: reviewStats?.connected && reviewStats?.average_rating ? `${reviewStats.average_rating}` : '--',
      icon: Star,
      color: 'orange',
      href: '/dashboard/reviews',
      description: reviewStats?.connected ? `${reviewStats.total_reviews || 0} reviews` : 'Not connected',
    },
  ];

  const colorClasses = {
    orange: 'bg-orange-100 text-orange-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-4 lg:p-8" data-testid="dashboard-overview">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="dashboard-title">
          Welcome back{tenant?.user?.first_name ? `, ${tenant.user.first_name}` : ''}!
        </h1>
        <p className="text-gray-600 mt-1">
          Here&apos;s what&apos;s happening with your campaigns
        </p>
      </div>

      {/* Signup Credit Banner - Only show if wallet has exactly $5 and no activity */}
      {walletBalance === 500 && recentActivity.length <= 1 && (
        <div className="mb-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Gift className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">You&apos;ve received $5 in free SMS credit!</p>
                <p className="text-sm text-green-100">
                  Send your first campaign to see how DAZZA works.
                </p>
              </div>
            </div>
            <Link 
              to="/dashboard/campaigns/new" 
              className="bg-white text-green-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-50 transition-colors text-center flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Send First Campaign
            </Link>
          </div>
        </div>
      )}

      {/* Low Balance Warning */}
      {walletBalance > 0 && walletBalance < 200 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <div>
                        <p className="font-medium text-yellow-800">Low wallet balance</p>
                <p className="text-sm text-yellow-600">
                  You have ${(walletBalance / 100).toFixed(2)} remaining (≈ {smsCapacity} SMS)
                </p>
              </div>
            </div>
            <Link 
              to="/dashboard/wallet" 
              className="bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-yellow-700 transition-colors text-center"
            >
              Top up wallet
            </Link>
          </div>
        </div>
      )}

      {/* Empty Wallet Warning */}
      {walletBalance === 0 && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <p className="font-medium text-red-800">Wallet empty</p>
                <p className="text-sm text-red-600">
                  Top up your wallet to send SMS campaigns
                </p>
              </div>
            </div>
            <Link 
              to="/dashboard/wallet" 
              className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors text-center"
            >
              Top up now
            </Link>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.name}
              to={stat.href}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6 hover:shadow-md transition-shadow group flex flex-col justify-between"
              data-testid={`stat-${stat.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div>
                <div className="flex items-center justify-between mb-3 lg:mb-4">
                  <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl ${colorClasses[stat.color]} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 lg:w-6 lg:h-6" />
                  </div>
                  <ArrowRight className="hidden lg:block w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </div>
                <div className="text-xl lg:text-3xl font-bold text-gray-900 mb-1 truncate">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500 font-medium truncate">{stat.name}</div>
              </div>
              <div className="text-xs text-gray-400 mt-1 truncate">{stat.description}</div>
            </Link>
          );
        })}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Getting Started Checklist */}
        <GettingStartedChecklist />

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            <Link to="/dashboard/wallet" className="text-sm text-orange-600 hover:text-orange-700">
              View all
            </Link>
          </div>
          
          {recentActivity.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No recent activity</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentActivity.map((activity) => (
                <div 
                  key={activity.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    activity.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {activity.type === 'credit' ? (
                      <ArrowUpRight className="w-4 h-4 text-green-600" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(activity.created_at)}
                    </p>
                  </div>
                  <div className={`text-sm font-medium whitespace-nowrap ${
                    activity.type === 'credit' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {activity.type === 'credit' ? '+' : '-'}
                    ${(activity.amount_cents / 100).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
